'use client';

import { useEffect, useRef, useState } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { locale } from '@/constants/locale';
import { cn } from '@/lib/utils';
import { useIsMounted } from '@/hooks/use-is-mounted';

const { common: commonLocale } = locale;

// The third-party embed has been seen to rate-limit, so the placeholder gives
// up rather than shimmering forever.
const GIVE_UP_MS = 8000;

type Props = {
  src: string;
  width: number;
  height: number;
};

export const TrustpilotEmbed = ({ src, width, height }: Props) => {
  const [settled, setSettled] = useState(false);
  const [scale, setScale] = useState(1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isMounted = useIsMounted();

  useEffect(() => {
    const timer = setTimeout(() => setSettled(true), GIVE_UP_MS);
    return () => clearTimeout(timer);
  }, []);

  // Narrowing the frame instead would reflow the embed's fixed-width layout
  // taller than `height`, leaving it scrollable inside its own box.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const observer = new ResizeObserver(([entry]) => {
      setScale(Math.min(1, entry.contentRect.width / width));
    });
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [width]);

  return (
    // Sized by the parent, never by the frame, so measuring it cannot loop.
    <div
      ref={wrapperRef}
      className="relative w-full overflow-hidden"
      style={{ maxWidth: width, height: height * scale }}
    >
      {!settled && <Skeleton className="absolute inset-0 rounded-lg" />}
      {/* Mounted after hydration: an SSR'd frame can finish loading before React
          attaches `onLoad`, stranding the placeholder for the full 8s. */}
      {isMounted && (
        <iframe
          src={src}
          title={commonLocale.badgeTrustpilotReviews}
          width={width}
          height={height}
          loading="lazy"
          onLoad={() => setSettled(true)}
          // Kept mounted while loading so the frame can actually fetch.
          className={cn(
            'trustpilot-embed absolute top-0 left-0 rounded-lg transition-opacity',
            settled ? 'opacity-100' : 'opacity-0'
          )}
          style={{
            border: 0,
            transform: `scale(${scale})`,
            transformOrigin: 'top left'
          }}
        />
      )}
    </div>
  );
};
